import { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, Navigate, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, Dumbbell, Apple, LineChart, Trophy, Sparkles, CalendarCheck, CalendarDays, Users2, Package, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { awardPoints } from "../lib/api";
import { toast } from "sonner";
import ThemeToggle from "./ThemeToggle";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/programs", label: "My Programs", icon: Package },
  { to: "/app/workouts", label: "Workouts", icon: Dumbbell },
  { to: "/app/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/app/nutrition", label: "Nutrition", icon: Apple },
  { to: "/app/progress", label: "Progress", icon: LineChart },
  { to: "/app/challenges", label: "Challenges", icon: Trophy },
  { to: "/app/community", label: "Community", icon: Users2 },
  { to: "/app/coach", label: "AI Assistant", icon: Sparkles },
  { to: "/app/booking", label: "Book Consult", icon: CalendarCheck },
];

export default function AppLayout() {
  const { user, loading, logout, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [profileBanner, setProfileBanner] = useState(() => {
    try { return localStorage.getItem("bffy_profile_banner_dismissed") !== "1"; } catch { return true; }
  });
  const navigate = useNavigate();
  const awarded = useRef(false);

  useEffect(() => {
    if (user && user.role === "user" && !awarded.current) {
      awarded.current = true;
      awardPoints("daily_login").then((pts) => { if (pts) { toast.success(`Daily check-in! +${pts} pts`); refresh(); } });
    }
  }, [user, refresh]);

  if (loading || user === null) return <div className="min-h-screen grid place-items-center bg-background text-muted-foreground">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  // NOTE: fitness profile (onboarding) is OPTIONAL — it must never block paid content or purchase.

  const doLogout = () => { logout(); navigate("/"); };

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <Link to="/" className="font-display font-900 uppercase text-2xl px-6 py-6 border-b border-border">Being<span className="text-primary">Fit</span>ForYou</Link>
      <nav className="flex-1 p-4 space-y-1">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`applink-${n.label.toLowerCase().replace(/\s/g,'-')}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-border flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-full bg-primary/15 text-primary grid place-items-center font-bold shrink-0">{(user.name || "U")[0].toUpperCase()}</div>
          <div className="min-w-0"><div className="text-sm font-bold truncate">{user.name}</div><div className="text-[11px] text-muted-foreground">{user.points} pts</div></div>
        </div>
        <button onClick={doLogout} data-testid="app-logout" className="text-muted-foreground hover:text-primary p-2" aria-label="Logout"><LogOut size={18} /></button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="w-64 shrink-0 border-r border-border bg-card hidden md:block sticky top-0 h-screen">{SidebarInner}</aside>

      {/* mobile */}
      {open && <div className="fixed inset-0 z-40 bg-black/50 md:hidden" onClick={() => setOpen(false)} />}
      <aside className={`fixed z-50 top-0 left-0 h-full w-64 bg-card border-r border-border md:hidden transition-transform ${open ? "translate-x-0" : "-translate-x-full"}`}>{SidebarInner}</aside>

      <div className="flex-1 min-w-0">
        <header className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 border-b border-border bg-background/90 backdrop-blur">
          <button onClick={() => setOpen(true)} data-testid="app-mobile-menu"><Menu /></button>
          <span className="font-display font-800 uppercase text-xl">Being<span className="text-primary">Fit</span></span>
          <ThemeToggle />
        </header>
        <div className="hidden md:flex justify-end px-8 pt-5"><ThemeToggle /></div>
        {user && !user.onboarding_complete && profileBanner && (
          <div data-testid="profile-banner" className="mx-4 md:mx-8 mt-3 bg-primary/10 border border-primary/30 rounded-lg px-4 py-2.5 text-xs flex items-center justify-between gap-3">
            <span>Want a personalised plan? <Link to="/onboarding" className="text-primary font-bold underline">Complete your fitness profile</Link> — optional, takes 1 min.</span>
            <button onClick={() => { setProfileBanner(false); try { localStorage.setItem("bffy_profile_banner_dismissed", "1"); } catch {} }} aria-label="Dismiss" className="text-muted-foreground hover:text-foreground shrink-0"><X size={14} /></button>
          </div>
        )}
        <Outlet />
      </div>
    </div>
  );
}
